import { useState, useEffect } from 'react';
import { pb } from '@/lib/pocketbase';
import { RecordModel } from 'pocketbase';

export interface TutorAgreement extends RecordModel {
  tutor_id: string;
  terms_version: string;
  agreement_type: 'initial_signup' | 'terms_update' | 'policy_change';
  agreed_at: string;
  ip_address: string;
  user_agent: string;
  terms_content: string;
  is_active: boolean;
}

export interface TermsVersion extends RecordModel {
  version: string;
  title: string;
  content: string;
  effective_date: string;
  is_current: boolean;
  agreement_type: 'tutor_terms' | 'privacy_policy' | 'refund_policy';
}

export const useTutorAgreements = () => {
  const [currentTerms, setCurrentTerms] = useState<TermsVersion | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const getCurrentTerms = async () => {
    try {
      setIsLoading(true);
      const terms = await pb.collection('terms_versions')
        .getFirstListItem('is_current = true && agreement_type = "tutor_terms"');
      setCurrentTerms(terms as TermsVersion);
      return terms as TermsVersion;
    } catch (error) {
      console.error('Error fetching current terms:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const checkTutorAgreement = async (tutorId: string): Promise<boolean> => {
    try {
      const currentTerms = await getCurrentTerms();
      console.log('useTutorAgreements: Current terms:', currentTerms);
      if (!currentTerms) {
        console.log('useTutorAgreements: No current terms found');
        return false;
      }

      console.log('useTutorAgreements: Checking agreement for tutor:', tutorId, 'version:', currentTerms.version);
      
      // Check if tutor has agreed to the current version specifically
      const agreement = await pb.collection('tutor_agreements')
        .getFirstListItem(`tutor_id = "${tutorId}" && terms_version = "${currentTerms.version}" && is_active = true`)
        .catch(() => null);

      console.log('useTutorAgreements: Found agreement:', !!agreement, agreement);
      return !!agreement;
    } catch (error) {
      console.error('Error checking tutor agreement:', error);
      return false;
    }
  };

  const hasAgreedToCurrentVersion = async (tutorId: string): Promise<boolean> => {
    return checkTutorAgreement(tutorId);
  };

  const createAgreement = async (data: {
    tutor_id: string;
    terms_version: string;
    agreement_type: 'initial_signup' | 'terms_update' | 'policy_change';
    terms_content: string;
  }) => {
    try {
      const agreementData = {
        ...data,
        agreed_at: new Date().toISOString(),
        ip_address: 'unknown', // In production, get from server
        user_agent: navigator.userAgent,
        is_active: true
      };

      const agreement = await pb.collection('tutor_agreements').create(agreementData);
      return agreement;
    } catch (error) {
      console.error('Error creating agreement:', error);
      throw error;
    }
  };

  const getTutorAgreements = async (tutorId: string): Promise<TutorAgreement[]> => {
    try {
      const agreements = await pb.collection('tutor_agreements')
        .getFullList({
          filter: `tutor_id = "${tutorId}"`,
          sort: '-created'
        });
      return agreements as TutorAgreement[];
    } catch (error) {
      console.error('Error fetching tutor agreements:', error);
      return [];
    }
  };

  useEffect(() => {
    getCurrentTerms();
  }, []);

  return {
    currentTerms,
    isLoading,
    getCurrentTerms,
    checkTutorAgreement,
    hasAgreedToCurrentVersion,
    createAgreement,
    getTutorAgreements
  };
};